import React from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import moment, { Moment } from "moment"
import { observer, inject } from "mobx-react"
import Store from "../stores/Store"
import InputAdornment from "@material-ui/core/InputAdornment"
import CalendarTodayIcon from "@material-ui/icons/CalendarToday"
import Grid from "@material-ui/core/Grid"
import { DatePicker } from "@material-ui/pickers"
import NumberInput from "./NumberInput"

interface SearchBarProps extends WithTranslation {
    store?: Store
}

interface SearchBarState {
    invalidDates: boolean;
    openCheckOut: boolean;
}

@inject("store")
@observer
class SearchBar extends React.Component<SearchBarProps, SearchBarState> {
    constructor (props: SearchBarProps) {
        super(props)

        this.state = {
            invalidDates: false,
            openCheckOut: false
        }
    }

    componentDidMount = () => {
        this.searchAction()
    }

    validateDates = () => {
        if (this.props.store!.checkIn >= this.props.store!.checkOut) {
            this.setState({ invalidDates: true })
            return false
        } else {
            this.setState({ invalidDates: false })
            return true
        }
    }

    setCheckIn = (mDate: Moment | null) => {
        if (mDate === null) {
            mDate = moment()
        }
        mDate.startOf("day")

        // Check if data was invalid
        const cI = moment(this.props.store?.checkIn)
        let wasInvalid
        if (!this.props.store!.availableDates.includes(cI.clone().add(1, "days").format("Y-MM-DD"))) {
            wasInvalid = true
        } else {
            wasInvalid = !this.props.store!.availableDates.includes(cI.format("Y-MM-DD"))
        }

        this.props.store!.setCheckIn(mDate.toDate())
        this.props.store!.setCheckOut(mDate.add(1, "days").toDate())

        // Don't open checkout date popup
        if (!wasInvalid) {
            setTimeout(() => {
                this.setState({
                    openCheckOut: true
                })
            }, 100)
        }
    }

    setCheckOut = (mDate: Moment | null) => {
        if (mDate === null) {
            mDate = moment()
            mDate.add(3, "days")
        }

        this.props.store!.setCheckOut(mDate.startOf("day").toDate())

        this.searchAction()
    }

    setGuests = (newValue: number) => {
        this.props.store!.setGuests(newValue)
    }

    searchAction = () => {
        if (this.validateDates()) {
            this.props.store!.searchRooms()
        } else {
            this.props.store!.clearSearchRooms()
        }
    }

    render () {
        return (
            <Grid container spacing={2} alignItems="center" justify="center">
                <Grid item xs={12} sm={5}>
                    <DatePicker
                        id="checkInDate"
                        autoOk
                        fullWidth
                        disablePast
                        disableToolbar
                        variant="inline"
                        format="l"
                        label={this.props.t("Check-in")}
                        value={this.props.store!.checkIn}
                        onChange={this.setCheckIn}
                        error={this.state.invalidDates}
                        InputLabelProps={{
                            shrink: true
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CalendarTodayIcon color="secondary" />
                                </InputAdornment>
                            )
                        }}
                        shouldDisableDate={(day: Moment | null) => {
                            if (day === null) {
                                return false
                            }
                            if (!this.props.store!.availableDates.includes(day.clone().add(1, "days").format("Y-MM-DD"))) {
                                return true
                            }
                            return !this.props.store!.availableDates.includes(day.format("Y-MM-DD"))
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <DatePicker
                        id="checkOutDate"
                        autoOk
                        fullWidth
                        disablePast
                        disableToolbar
                        variant="inline"
                        format="l"
                        label={this.props.t("Check-out")}
                        value={this.props.store!.checkOut}
                        onChange={this.setCheckOut}
                        error={this.state.invalidDates}
                        InputLabelProps={{
                            shrink: true
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CalendarTodayIcon color="secondary" />
                                </InputAdornment>
                            )
                        }}
                        shouldDisableDate={(day: Moment | null) => {
                            if (day === null) {
                                return false
                            }
                            return !this.props.store!.availableDates.includes(day.format("Y-MM-DD"))
                        }}
                        open={this.state.openCheckOut}
                        onOpen={() => { this.setState({ openCheckOut: true }) }}
                        onClose={() => { this.setState({ openCheckOut: false }) }}
                    />
                </Grid>
                <Grid item xs={4} sm={2}>
                    <NumberInput
                        label={this.props.t("Guests")}
                        defaultValue={this.props.store!.guests}
                        onChange={this.setGuests}
                    />
                </Grid>
            </Grid>
        )
    }
}

export default withTranslation()(SearchBar)
